import React from 'react';
import { SummaryRow, BoolIcon, Badges } from '../../../components/SummaryRow';
import { TagLabels } from '../../../components/TagPill';
import type { ExportFormState } from '../../../types/export';

interface Props {
  formState: ExportFormState;
  stats: { resourceCount: number; pageCount: number; size: number } | null;
}

function formatBytes(n: number): string {
  if (n < 1e6) return (n / 1e3).toFixed(1) + 'KB';
  if (n < 1e9) return (n / 1e6).toFixed(1) + 'MB';
  return (n / 1e9).toFixed(2) + 'GB';
}

export function Step4Summary({ formState, stats }: Props) {
  const f = formState;

  return (
    <div>
      <div className="pull-right">
        <ul className="nav nav-pills">
          <li><a href="#">Resources <span className="badge">{stats?.resourceCount ?? 0}</span></a></li>
          <li><a href="#">Pages <span className="badge">{stats?.pageCount ?? 0}</span></a></li>
          <li><a href="#">Size <span className="badge">{formatBytes(stats?.size ?? 0)}</span></a></li>
        </ul>
      </div>

      <div style={{ clear: 'both', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SummaryRow label="Name">{f.name}</SummaryRow>
        <SummaryRow label="Color Setting">{f.colorSetting}</SummaryRow>
        <SummaryRow label="Resources with Processing Exceptions">
          <BoolIcon value={f.includeResourcesWithProcessingExceptions} />
        </SummaryRow>
        <SummaryRow label="Resources with Zero Pages">
          <BoolIcon value={f.includeResourcesWithZeroPages} />
        </SummaryRow>
        <SummaryRow label="Duplicate Resources">
          <BoolIcon value={f.includeDuplicateResources} />
        </SummaryRow>
        <SummaryRow label="Redacted Resources">
          <BoolIcon value={f.includeRedactedResources} />
        </SummaryRow>
        <SummaryRow label="Keep Original Page Orientation">
          <BoolIcon value={f.keepPageOrientation} />
        </SummaryRow>
        <SummaryRow label="Collections">
          <Badges items={f.collectionNames} />
        </SummaryRow>
        {f.tags.and.length > 0 && (
          <SummaryRow label="And Tags"><TagLabels tags={f.tags.and} /></SummaryRow>
        )}
        {f.tags.or.length > 0 && (
          <SummaryRow label="Or Tags"><TagLabels tags={f.tags.or} /></SummaryRow>
        )}
        {f.tags.not.length > 0 && (
          <SummaryRow label="Not Tags"><TagLabels tags={f.tags.not} /></SummaryRow>
        )}
        {f.containerExportTag?.name && (
          <SummaryRow label="Container Export Tag">{f.containerExportTag.name}</SummaryRow>
        )}
        <SummaryRow label="Load File Formats">
          <Badges items={f.loadFileFormatNames} />
        </SummaryRow>
        {f.outputTypes.length > 0 && (
          <>
            <SummaryRow label="Output Types">
              <Badges items={[...f.outputTypes].sort()} />
            </SummaryRow>
            <SummaryRow label="Placeholder Settings">
              <Badges items={[
                'Missing Images',
                ...(f.spreadsheetPlaceholder ? ['Spreadsheets'] : []),
                ...(f.presentationPlaceholder ? ['Presentations'] : []),
                ...(f.tagPlaceholder?.name ? [`Documents Tagged with ${f.tagPlaceholder.name}`] : []),
              ]} />
            </SummaryRow>
          </>
        )}
        <SummaryRow label="Prefix">{f.prefix}</SummaryRow>
        <SummaryRow label="Start value">{f.startValue}</SummaryRow>
        <SummaryRow label="Zero padding">{f.zeroPaddingLength}</SummaryRow>
        <SummaryRow label="Files per directory">{f.filesPerDirectory}</SummaryRow>
        <SummaryRow label="Virus Placeholder Text">{f.virusPlaceholderText}</SummaryRow>
        <SummaryRow label="Native Format Placeholder Text">{f.nativeFormatPlaceholderText}</SummaryRow>
        <SummaryRow label="Failed to Image Placeholder">{f.failedToImagePlaceholderText}</SummaryRow>
        <SummaryRow label="South West Endorsement">{f.southWestEndorsement[0]?.name ?? '—'}</SummaryRow>
        <SummaryRow label="South Endorsement">{f.southEndorsement[0]?.name ?? '—'}</SummaryRow>
        <SummaryRow label="South East Endorsement">{f.southEastEndorsement[0]?.name ?? '—'}</SummaryRow>
      </div>
    </div>
  );
}
